/**
 * Core theme utilities and shared custom elements.
 * Everything here is design-agnostic: behaviour binds to data-* hooks so the
 * markup in sections/ can change without touching this file.
 */

/* ------------------------------------------------------------------ *
 * Small helpers
 * ------------------------------------------------------------------ */

const Theme = {
  routes: window.themeRoutes || {},
  strings: window.themeStrings || {},

  /** Debounce that preserves the latest arguments. */
  debounce(fn, wait = 300) {
    let timer;
    return function debounced(...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  },

  /** Fetch config for Shopify's Cart AJAX API. */
  fetchConfig(type = 'json') {
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/' + type,
      },
    };
  },

  /** Formats a price in cents using the shop's money format. */
  formatMoney(cents) {
    const format = window.themeMoneyFormat || '${{amount}}';
    const value = (cents / 100).toFixed(2);
    const parts = value.split('.');
    const withCommas = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format
      .replace(/\{\{\s*amount\s*\}\}/, withCommas + '.' + parts[1])
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, withCommas + ',' + parts[1]);
  },

  /** Traps focus inside a container while it is open. */
  trapFocus(container, elementToFocus) {
    const focusable = Array.from(
      container.querySelectorAll(
        'summary, a[href], button:enabled, [tabindex]:not([tabindex^="-"]), area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, iframe'
      )
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    this.removeTrapFocus();

    this._trapHandler = (event) => {
      if (event.key !== 'Tab') return;
      if (event.target === last && !event.shiftKey) {
        event.preventDefault();
        if (first) first.focus();
      }
      if ((event.target === container || event.target === first) && event.shiftKey) {
        event.preventDefault();
        if (last) last.focus();
      }
    };

    document.addEventListener('keydown', this._trapHandler);
    (elementToFocus || container).focus({ preventScroll: true });
  },

  removeTrapFocus(elementToFocus) {
    if (this._trapHandler) {
      document.removeEventListener('keydown', this._trapHandler);
      this._trapHandler = null;
    }
    if (elementToFocus) elementToFocus.focus();
  },

  /** Locks body scroll without the iOS scroll jump. */
  lockScroll() {
    if (document.body.hasAttribute('data-scroll-locked')) return;
    const scrollY = window.scrollY;
    document.body.dataset.scrollLocked = String(scrollY);
    document.body.style.position = 'fixed';
    document.body.style.top = '-' + scrollY + 'px';
    document.body.style.width = '100%';
  },

  unlockScroll() {
    const scrollY = document.body.dataset.scrollLocked;
    if (scrollY === undefined) return;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    delete document.body.dataset.scrollLocked;
    window.scrollTo(0, parseInt(scrollY, 10));
  },

  /** Fetches a section's rendered HTML via the Section Rendering API. */
  async renderSection(sectionId, url, params) {
    const path = url || window.location.pathname;
    const query = 'sections=' + sectionId + (params ? '&' + params : '');
    const response = await fetch(path + '?' + query);
    if (!response.ok) throw new Error('Section ' + sectionId + ' failed to render');
    const data = await response.json();
    return data[sectionId];
  },
};

window.Theme = Theme;

/* ------------------------------------------------------------------ *
 * <theme-drawer> — slide-in panel (cart, mobile nav, filters)
 * ------------------------------------------------------------------ */

class ThemeDrawer extends HTMLElement {
  connectedCallback() {
    this.overlay = this.querySelector('[data-drawer-overlay]');
    this.panel = this.querySelector('[data-drawer-panel]');

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-drawer-close]') || event.target === this.overlay) {
        this.close();
      }
    });

    this._onKeydown = (event) => {
      if (event.key === 'Escape' && this.open) this.close();
    };
    document.addEventListener('keydown', this._onKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._onKeydown);
  }

  get open() {
    return this.hasAttribute('open');
  }

  show(openerElement) {
    this.opener = openerElement || null;
    this.setAttribute('open', '');
    Theme.lockScroll();
    // Wait a frame so the CSS transition runs from the closed state.
    requestAnimationFrame(() => {
      this.setAttribute('data-visible', '');
      const target = (this.panel && this.panel.querySelector('[data-drawer-close]')) || this.panel || this;
      Theme.trapFocus(this, target);
    });
    this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
  }

  close() {
    this.removeAttribute('data-visible');
    Theme.removeTrapFocus(this.opener);
    Theme.unlockScroll();
    const finish = () => this.removeAttribute('open');
    const duration = parseFloat(getComputedStyle(this.panel || this).transitionDuration) * 1000;
    if (duration > 0) {
      setTimeout(finish, duration);
    } else {
      finish();
    }
    this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
  }
}
customElements.define('theme-drawer', ThemeDrawer);

/* Any element with [data-drawer-open="drawer-id"] opens that drawer. */
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-drawer-open]');
  if (!trigger) return;
  const drawer = document.getElementById(trigger.dataset.drawerOpen);
  if (!drawer) return;
  event.preventDefault();
  drawer.show(trigger);
});

/* ------------------------------------------------------------------ *
 * <quantity-input> — stepper used in cart lines and product forms
 * ------------------------------------------------------------------ */

class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    if (!this.input) return;
    this.changeEvent = new Event('change', { bubbles: true });

    this.querySelectorAll('button').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        const previous = this.input.value;
        if (button.name === 'plus') {
          this.input.stepUp();
        } else {
          this.input.stepDown();
        }
        if (previous !== this.input.value) this.input.dispatchEvent(this.changeEvent);
        this.toggleButtons();
      });
    });

    this.input.addEventListener('change', () => this.toggleButtons());
    this.toggleButtons();
  }

  toggleButtons() {
    const value = parseInt(this.input.value, 10);
    const min = parseInt(this.input.min || '0', 10);
    const max = this.input.max ? parseInt(this.input.max, 10) : null;
    const minus = this.querySelector('button[name="minus"]');
    const plus = this.querySelector('button[name="plus"]');
    if (minus) minus.disabled = value <= min;
    if (plus) plus.disabled = max !== null && value >= max;
  }
}
customElements.define('quantity-input', QuantityInput);

/* ------------------------------------------------------------------ *
 * <accordion-item> — animated <details>, used by FAQ, product tabs,
 * mobile nav and collection filter groups.
 * ------------------------------------------------------------------ */

class AccordionItem extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector('details');
    this.summary = this.querySelector('summary');
    this.content = this.querySelector('[data-accordion-content]');
    if (!this.details || !this.summary || !this.content) return;

    this.summary.addEventListener('click', (event) => {
      event.preventDefault();
      if (this.details.open) {
        this.collapse();
      } else {
        this.expand();
      }
    });
  }

  expand() {
    const group = this.closest('[data-accordion-single]');
    if (group) {
      group.querySelectorAll('accordion-item').forEach((item) => {
        if (item !== this) item.collapse();
      });
    }
    this.details.open = true;
    this.content.style.height = '0px';
    requestAnimationFrame(() => {
      this.content.style.height = this.content.scrollHeight + 'px';
    });
    this.content.addEventListener(
      'transitionend',
      () => {
        this.content.style.height = 'auto';
      },
      { once: true }
    );
    this.summary.setAttribute('aria-expanded', 'true');
  }

  collapse() {
    if (!this.details || !this.details.open) return;
    this.content.style.height = this.content.scrollHeight + 'px';
    requestAnimationFrame(() => {
      this.content.style.height = '0px';
    });
    this.content.addEventListener(
      'transitionend',
      () => {
        this.details.open = false;
      },
      { once: true }
    );
    this.summary.setAttribute('aria-expanded', 'false');
  }
}
customElements.define('accordion-item', AccordionItem);

/* ------------------------------------------------------------------ *
 * <sticky-header> — hides on scroll down, reveals on scroll up
 * ------------------------------------------------------------------ */

class StickyHeader extends HTMLElement {
  connectedCallback() {
    this.lastScroll = window.scrollY;
    this.threshold = parseInt(this.dataset.threshold || '80', 10);
    this._onScroll = Theme.debounce(() => this.handleScroll(), 10);
    window.addEventListener('scroll', this._onScroll, { passive: true });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this._onScroll);
  }

  handleScroll() {
    const current = window.scrollY;
    this.toggleAttribute('data-scrolled', current > this.threshold);
    // Never hide while a drawer or dropdown owns the screen.
    if (document.body.hasAttribute('data-scroll-locked')) return;
    if (current > this.lastScroll && current > this.threshold * 2) {
      this.setAttribute('data-hidden', '');
    } else {
      this.removeAttribute('data-hidden');
    }
    this.lastScroll = current;
  }
}
customElements.define('sticky-header', StickyHeader);
